import React, { useState, useEffect } from 'react';
import MessageBubble from './MessageBubble';
import ThinkingIndicator from './ThinkingIndicator';
import QueuedFactDisplay from './QueuedFactDisplay';
import styles from '../styles/ChatArea.module.css';

function ChatArea({ messages, isLoading, conversationId, currentMission, loadingMessage, onPlanEdit, onArticleClick, onNewsClick, onCoachClick, onQueuedFactChange }) {
  const [hasQueuedFact, setHasQueuedFact] = useState(false);
  const [checkingForFact, setCheckingForFact] = useState(false);

  // Check for queued fact when loading starts
  useEffect(() => {
    // Only check when loading starts (not when it ends)
    if (!isLoading) {
      setCheckingForFact(false);
      return;
    }

    // Only check once when loading starts
    if (checkingForFact || hasQueuedFact) {
      return;
    }

    setCheckingForFact(true);

    // Quick check if there's a queued fact
    const token = localStorage.getItem('authToken');
    if (!token) {
      setCheckingForFact(false);
      return;
    }

    // Add AbortController to cancel request if component unmounts or isLoading changes
    const controller = new AbortController();

    fetch('/api/loading-screen/check-queued', {
      headers: { 'Authorization': `Bearer ${token}` },
      signal: controller.signal // Cancel if new request starts
    })
      .then(async res => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }

        // Check if response is JSON before parsing
        const contentType = res.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Server returned non-JSON response (possibly HTML error page)');
        }

        return res.json();
      })
      .then(data => {
        setHasQueuedFact(data.hasFact);
        setCheckingForFact(false);
      })
      .catch(err => {
        // Ignore AbortError - it's expected when request is cancelled
        if (err.name !== 'AbortError') {
          console.error('[ChatArea] Failed to check for queued fact:', err);
        }
        setHasQueuedFact(false);
        setCheckingForFact(false);
      });

    // Cleanup: cancel request if component unmounts or isLoading changes
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]); // Only depend on isLoading - internal state shouldn't trigger re-runs

  // Notify parent when queued fact state changes
  useEffect(() => {
    if (onQueuedFactChange) {
      onQueuedFactChange(hasQueuedFact);
    }
  }, [hasQueuedFact, onQueuedFactChange]);

  // Show only the last assistant message (skip user messages if they're last)
  const getDisplayMessages = () => {
    // IMPORTANT: If loading, don't show any messages - just show loading indicator
    // This clears the previous response when user sends a new message
    if (isLoading) {
      return [];
    }

    if (messages.length === 0) return [];

    const lastMessage = messages[messages.length - 1];

    // If last message is from assistant, show it
    if (lastMessage.role === 'assistant') {
      return [lastMessage];
    }

    // If last message is from user, find the previous assistant message
    for (let i = messages.length - 2; i >= 0; i--) {
      if (messages[i].role === 'assistant') {
        return [messages[i]];
      }
    }

    // No assistant messages found
    return [];
  };

  const displayMessages = getDisplayMessages();

  return (
    <div className={styles.chatArea}>
      <div className={styles.messagesContainer}>
        {/* Show queued fact if one exists - persist even after loading completes */}
        {hasQueuedFact ? (
          <QueuedFactDisplay
            isStillLoading={isLoading}
            onDismiss={() => {
              setHasQueuedFact(false);
              // Will show messages after user clicks Continue
            }}
          />
        ) : isLoading ? (
          // Show normal loading indicator with pre-fetched message
          <ThinkingIndicator loadingMessage={loadingMessage} />
        ) : (
          // Show messages when not loading
          displayMessages.map((message, index) => (
            <MessageBubble
              key={message.id || index}
              role={message.role}
              content={message.content}
              timestamp={message.timestamp}
              conversationId={conversationId}
              currentMission={currentMission}
              onPlanEdit={onPlanEdit}
              onArticleClick={onArticleClick}
              onNewsClick={onNewsClick}
              onCoachClick={onCoachClick}
            />
          ))
        )}
        {/* Empty spacer div to ensure content isn't hidden behind input field or progress bar changes */}
        <div style={{ height: '100px', flexShrink: 0, width: '100%' }}></div>
      </div>
    </div>
  );
}

export default ChatArea;
